import { text } from 'stream/consumers'

import { useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Briefcase,
  Building,
  Calendar,
  CheckCircle,
  Circle,
  Clock,
  FileText,
  MapPin,
} from 'lucide-react'

import { queryJobHistoryUsers } from '@/services/job/query'

export default function ActiveApplications() {
  const historyUsersQry = useSuspenseQuery(queryJobHistoryUsers())
  const historyUsers = historyUsersQry.data

  // Use real data from API instead of static data
  const jobApplications = historyUsers?.data || []

  const availableSlots = {
    active: jobApplications.length,
    total: Math.max(0, 10 - jobApplications.length), // Assuming max 10 applications
  }

  // Status color mapping
  const getStatusColor = (status: string): { color: string; text: string } => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return {
          color: 'bg-yellow-100 text-yellow-800',
          text: 'In Progress',
        }
      case 'approved':
        return {
          color: 'bg-green-100 text-green-800',
          text: 'Terima',
        }
      case 'rejected':
        return {
          color: 'bg-red-100 text-red-800',
          text: 'Ditolak',
        }
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          text: 'Pending',
        }
    }
  }

  // Stage color mapping
  const getStageColor = (stage: string) => {
    switch (stage?.toLowerCase()) {
      default:
        return 'text-gray-600'
    }
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-2 py-4 sm:px-4 sm:py-6 lg:px-6">
      {/* Job Applications */}
      <div className="mb-6 space-y-4 sm:mb-8 sm:space-y-6">
        {jobApplications.length > 0 ? (
          jobApplications.map((job) => (
            <div
              key={job.id}
              className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-md"
            >
              {/* Simplified Job Card */}
              <div className="p-4 sm:p-6">
                <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                  {/* Left Section - Job Info */}
                  <div className="flex-1 space-y-3">
                    {/* Position */}
                    <div className="flex items-start space-x-3">
                      <div className="mt-1 flex-shrink-0">
                        <Briefcase className="h-5 w-5 text-primary-yes" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="break-words text-base font-semibold leading-tight text-gray-900 sm:text-lg">
                          {job.position}
                        </h3>
                      </div>
                    </div>

                    {/* Current Stage */}
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <Clock className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <span
                          className={`text-sm font-medium ${getStageColor(job.currentStage)}`}
                        >
                          Tahap: {job.currentStage}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Section - Status */}
                  <div className="flex flex-col items-start space-y-2 sm:items-end">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium sm:text-sm ${getStatusColor(job.status).color}`}
                    >
                      {getStatusColor(job.status).text}
                    </span>
                    <span className="text-xs text-gray-500 sm:text-sm">
                      ID: #{job.id}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-3 sm:space-y-0">
                    <Link
                      to="/job-detail/$jobId"
                      params={{ jobId: String(job.id) }}
                    >
                      <button className="w-full rounded-lg bg-gray-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600 sm:w-auto sm:px-6">
                        Detail Pekerjaan
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          // Empty State
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
              <FileText className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-gray-600">
              Belum Ada Lamaran Aktif
            </h3>
            <p className="mb-6 text-gray-500">
              Anda belum memiliki lamaran pekerjaan yang sedang diproses
            </p>
            <button
              className="rounded-lg px-6 py-3 font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: '#D91F2E' }}
              onClick={() => (window.location.href = '/')}
            >
              Jelajahi Lowongan Kerja
            </button>
          </div>
        )}
      </div>

      {/* Available Slot Section - Only show if there are applications */}
      {/* {jobApplications.length > 0 && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-4 sm:p-8">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 sm:h-16 sm:w-16">
              <Calendar className="h-6 w-6 text-red-500 sm:h-8 sm:w-8" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900 sm:text-xl">
              Slot Lamaran
            </h3>
            <p className="mb-2 text-sm text-gray-600 sm:text-base">
              [{availableSlots.active}] Lamaran aktif sedang diproses.
            </p>
            <p className="mb-4 text-sm text-gray-600 sm:mb-6 sm:text-base">
              Masih ada [{availableSlots.total}] slot tersedia untuk lamaran baru.
            </p>
            <button
              className="w-full rounded-lg px-6 py-2 text-sm font-semibold text-white transition-colors hover:opacity-90 sm:w-auto sm:px-8 sm:py-3 sm:text-base"
              style={{ backgroundColor: '#D91F2E' }}
              onClick={() => window.location.href = '/'}
            >
              Lamar Pekerjaan Sekarang
            </button>
          </div>
        </div>
      )} */}
    </div>
  )
}
